import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, login as apiLogin, register as apiRegister, googleSession, fetchPublicSettings, TOKEN_KEY, USER_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem("tm_settings");
      if (cached) return JSON.parse(cached);
    } catch {}
    return { refresh_interval_minutes: 15, ai_enabled: true };
  });
  const [creatorMode, setCreatorMode] = useState(() => {
    try {
      return localStorage.getItem("tm_creator_mode") === "1";
    } catch {
      return false;
    }
  });

  const toggleCreatorMode = useCallback(() => {
    setCreatorMode((v) => {
      const next = !v;
      try {
        localStorage.setItem("tm_creator_mode", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const persist = useCallback((token, userObj) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (userObj) localStorage.setItem(USER_KEY, JSON.stringify(userObj));
    setUser(userObj);
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const u = await fetchMe();
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
      return u;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
      return null;
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const s = await fetchPublicSettings();
      setSettings(s);
      try { localStorage.setItem("tm_settings", JSON.stringify(s)); } catch {}
    } catch {
      /* keep defaults */
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([refreshUser(), refreshSettings()]);
      setLoading(false);
    })();
  }, [refreshUser, refreshSettings]);

  const login = async (email, password) => {
    const data = await apiLogin({ email, password });
    persist(data.token, data.user);
    await refreshSettings();
    return data.user;
  };

  const register = async (payload) => {
    const data = await apiRegister(payload);
    persist(data.token, data.user);
    await refreshSettings();
    return data.user;
  };

  const loginWithGoogleSession = async (session_id, country, timezone) => {
    const data = await googleSession(session_id, country, timezone);
    persist(data.token, data.user);
    await refreshSettings();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const requireAuth = (cb) => {
    if (user) return cb?.();
    setAuthModalOpen(true);
    return null;
  };

  const value = {
    user,
    loading,
    settings,
    refreshSettings,
    refreshUser,
    login,
    register,
    loginWithGoogleSession,
    logout,
    requireAuth,
    authModalOpen,
    setAuthModalOpen,
    isAdmin: user?.role === "admin",
    creatorMode: !!user && creatorMode,
    toggleCreatorMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
